import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faMusic, faPlay, faForward, faBackward } from '@fortawesome/free-solid-svg-icons';
import { faDiscord, faSpotify } from '@fortawesome/free-brands-svg-icons';
import * as discordApi from '../services/discordApi';
import type { DiscordMember } from '../services/discordApi';

interface MusicProps {
  onBack: () => void;
}

interface SpotifyActivity {
  member: DiscordMember;
  songName: string;
  artist: string;
  album?: string;
}

function Music({ onBack }: MusicProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [onlineMembers, setOnlineMembers] = useState<DiscordMember[]>([]);
  const [spotifyListeners, setSpotifyListeners] = useState<SpotifyActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMusicData = async () => {
      try {
        setLoading(true);
        const response = await discordApi.getOnlineMembers();

        if (response.success && response.data) {
          setOnlineMembers(response.data);

          // Extract Spotify activities
          const spotify: SpotifyActivity[] = [];
          response.data.forEach(member => {
            member.activities?.forEach(activity => {
              // Type 2 = Listening (Spotify)
              if (activity.type === 2 && activity.name === 'Spotify') {
                spotify.push({
                  member,
                  songName: activity.details || 'Unknown Song',
                  artist: activity.state || 'Unknown Artist',
                  album: undefined,
                });
              }
            });
          });

          setSpotifyListeners(spotify);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching music data:', error);
        setLoading(false);
      }
    };

    fetchMusicData();

    // Refresh every 30 seconds for music status
    const interval = setInterval(fetchMusicData, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen p-8 lg:ml-64 lg:mr-80 bg-gradient-to-br from-[#9b5de5] to-[#f582ae]">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="mb-4 bg-white border-4 border-black px-6 py-3 font-black uppercase transition-all hover:translate-x-1 hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
            Back
          </button>

          <h1 className="text-5xl md:text-7xl font-black mb-4 text-center">
            MUSIC
          </h1>
        </div>

        {loading ? (
          <div className="bg-white border-4 border-black p-12 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] text-center">
            <p className="text-2xl font-black">Loading music data...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Currently Listening on Spotify */}
            <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <h2 className="text-3xl font-black mb-4 uppercase flex items-center gap-2">
                <FontAwesomeIcon icon={faSpotify} className="text-[#1DB954]" />
                Currently Listening
              </h2>

              {spotifyListeners.length === 0 ? (
                <div className="bg-gray-100 border-4 border-black p-6 text-center">
                  <p className="font-bold">No one is listening to Spotify right now</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {spotifyListeners.map((listener, index) => (
                    <div
                      key={index}
                      className="bg-[#1DB954] border-4 border-black p-4 flex items-center gap-4"
                    >
                      <img
                        src={discordApi.getAvatarUrl(listener.member)}
                        alt={listener.member.username}
                        className="w-16 h-16 rounded-full border-4 border-black"
                      />
                      <div className="flex-1">
                        <p className="font-black text-white text-lg">{listener.songName}</p>
                        <p className="font-bold text-white/90">by {listener.artist}</p>
                        <p className="font-bold text-white/70 text-sm mt-1">
                          {listener.member.displayName || listener.member.username}
                        </p>
                      </div>
                      <div className="text-white text-2xl">
                        <FontAwesomeIcon icon={faMusic} className="animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Squad Playlists */}
            <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <h2 className="text-3xl font-black mb-4 uppercase flex items-center gap-2">
                <FontAwesomeIcon icon={faMusic} />
                Squad Playlists
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Placeholder Playlists */}
                <div className="bg-[#ffd803] border-4 border-black p-6 text-center cursor-pointer transition-all hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <FontAwesomeIcon icon={faMusic} className="text-4xl mb-2" />
                  <p className="font-black text-xl">Chill Vibes</p>
                  <p className="font-bold text-sm">45 songs</p>
                </div>

                <div className="bg-[#8bd3dd] border-4 border-black p-6 text-center cursor-pointer transition-all hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <FontAwesomeIcon icon={faMusic} className="text-4xl mb-2" />
                  <p className="font-black text-xl">Gaming Hype</p>
                  <p className="font-bold text-sm">67 songs</p>
                </div>

                <div className="bg-[#f582ae] border-4 border-black p-6 text-center cursor-pointer transition-all hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <FontAwesomeIcon icon={faMusic} className="text-4xl mb-2" />
                  <p className="font-black text-xl">Party Mix</p>
                  <p className="font-bold text-sm">82 songs</p>
                </div>

                <div className="bg-[#b8f2e6] border-4 border-black p-6 text-center cursor-pointer transition-all hover:translate-x-1 hover:translate-y-1 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                  <FontAwesomeIcon icon={faMusic} className="text-4xl mb-2" />
                  <p className="font-black text-xl">Late Night</p>
                  <p className="font-bold text-sm">53 songs</p>
                </div>
              </div>
            </div>

            {/* Discord Voice Music Bot Controls (Placeholder) */}
            <div className="bg-white border-4 border-black p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <h2 className="text-3xl font-black mb-4 uppercase flex items-center gap-2">
                <FontAwesomeIcon icon={faDiscord} className="text-[#5865F2]" />
                Discord Music Bot
              </h2>

              <div className="bg-gray-100 border-4 border-black p-8">
                <p className="font-bold text-center mb-4">
                  Music bot controls coming soon...
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    disabled
                    className="bg-gray-300 border-4 border-black px-6 py-3 font-black opacity-50 cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faBackward} />
                  </button>
                  <button
                    disabled
                    className="bg-gray-300 border-4 border-black px-8 py-3 font-black opacity-50 cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faPlay} />
                  </button>
                  <button
                    disabled
                    className="bg-gray-300 border-4 border-black px-6 py-3 font-black opacity-50 cursor-not-allowed"
                  >
                    <FontAwesomeIcon icon={faForward} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Music;
